import { useCallback, useMemo, useState } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { FiCheck, FiCornerUpLeft } from "react-icons/fi";
import { useCycle } from "../../context/CycleContext";
import { getEmployeeGoalSheet, approveGoalSheet, requestGoalChanges, getGoalChangeRequests, decideGoalChange } from "../../services/goalApi";
import useAsync from "../../hooks/useAsync";
import useFormDraft from "../../hooks/useFormDraft";
import { Card, PageHeader, Loader, ErrorState, Button, FormField, Banner, StatusBadge, ConfirmDialog, DataTable, DraftNotice } from "../../components/common";
import WeightageMeter from "../../components/goals/WeightageMeter";
import GoalList from "../../components/goals/GoalList";
import ActivityTimeline from "../../components/goals/ActivityTimeline";
import OwnershipPanel from "../../components/goals/OwnershipPanel";
import useReviewerGoalEdit, { REVIEWER_GOAL_EDIT } from "../../components/goals/ReviewerGoalEdit"; // [NEW] PM-15
import { GOAL_STATUS, SHEET_STATUS, MAX_GOALS_PER_YEAR } from "../../utils/constants";
import { getErrorMessage } from "../../utils/errors";

const api = { getEmployeeGoalSheet, approveGoalSheet, requestGoalChanges, getGoalChangeRequests, decideGoalChange };

export default function GoalApprovalReviewPage() {
  const { employeeId } = useParams();
  const { cycleId, cycle } = useCycle();
  const navigate = useNavigate();
  const { refreshCounters } = useOutletContext() || {};
  const { data, error, reload } = useAsync(() => api.getEmployeeGoalSheet(cycleId, employeeId), [cycleId, employeeId], { skip: !cycleId });
  const [comment, setComment] = useState("");
  const [goalComments, setGoalComments] = useState({});
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const recordId = data?.recordId;
  const changes = useAsync(() => api.getGoalChangeRequests(recordId), [recordId], { skip: !recordId });
  const [deciding, setDeciding] = useState(null);
  // [NEW] PM-15: with the backend switch on, the manager can correct a goal directly while reviewing the sheet.
  const goalEdit = useReviewerGoalEdit({
    goals: data?.goals || [], editedAs: "MANAGER", employeeName: data?.employee?.name, cycle,
    enabled: Boolean(data?.canAct && data?.sheetStatus === SHEET_STATUS.PENDING), onSaved: reload,
  });

  // [NEW] Keep the manager's unsent comments (overall + per goal) for this employee's sheet.
  const reviewText = useMemo(() => ({ comment, goalComments }), [comment, goalComments]);
  const setReviewText = useCallback((v) => { setComment(v.comment || ""); setGoalComments(v.goalComments || {}); }, []);
  const draft = useFormDraft(`goal-review:${cycleId}:${employeeId}`, reviewText, setReviewText,
    { active: Boolean(data?.canAct && data?.sheetStatus === SHEET_STATUS.PENDING) });

  const decideChange = async () => {
    setBusy(true);
    try {
      await api.decideGoalChange(deciding.row.id, deciding.approve, deciding.reason, { itemType: deciding.row.itemType });
      toast.success(deciding.approve ? "Change approved. The goal sheet is back in draft for the employee to resubmit." : "Change request rejected.");
      setDeciding(null);
      changes.reload();
      reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <Loader />;

  const pending = data.sheetStatus === SHEET_STATUS.PENDING;
  const canAct = data.canAct && pending;
  const isReviewable = (g) => [GOAL_STATUS.SUBMITTED, GOAL_STATUS.MANAGER_REVIEW].includes(g.status);

  const run = async () => {
    if (action === "changes" && !comment.trim()) {
      toast.error("Add a comment so the employee knows what to change.");
      return;
    }
    setBusy(true);
    try {
      const payload = { comment, goalComments };
      if (action === "approve") await api.approveGoalSheet(cycleId, employeeId, payload);
      else await api.requestGoalChanges(cycleId, employeeId, payload);
      toast.success(action === "approve" ? `Goals approved for ${data.employee.name}.` : `Changes requested from ${data.employee.name}.`);
      draft.clear();
      setAction(null);
      refreshCounters?.();
      navigate("/team/goal-approvals");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        backTo="/team/goal-approvals"
        backLabel="Goal approvals"
        title={`${data.employee.name}'s goals`}
        subtitle={`${data.employee.designationName}, ${data.employee.departmentName}. ${cycle?.name}`}
        actions={<StatusBadge status={data.sheetStatus} />}
      />

      <DraftNotice draft={draft} />

      {!pending && (
        <Banner tone="neutral" title="Nothing to act on">
          This sheet is {data.sheetStatus.toLowerCase()}. You can still read the goals and history.
        </Banner>
      )}

      {data.weightIssues.length > 0 && (
        <Banner tone="warning" title="HR must fix the KRA / KPI weights before you approve">
          {data.weightIssues.join(" ")} If approved like this, the final score could not be calculated at the end of the review.
        </Banner>
      )}

      {data.goalUsage && data.goalUsage.used > MAX_GOALS_PER_YEAR && (
        <Banner tone="warning" title={`More than ${MAX_GOALS_PER_YEAR} goals for ${data.goalUsage.yearLabel}`}>
          This employee has {data.goalUsage.used} goals for the year (maximum {MAX_GOALS_PER_YEAR}). Use Request changes and ask them to remove the extra goals.
        </Banner>
      )}

      <Card title="Goal percentage check">
        <WeightageMeter goals={data.goals} />
      </Card>

      <Card title="Who owns what" subtitle={REVIEWER_GOAL_EDIT ? "You can edit a goal here before you approve (give a reason), or use Request changes. After approval, changes go through a change request you approve." : "To change an employee's goal or weight, use Request changes. After approval, changes go through a change request you approve."}>
        <OwnershipPanel view="manager" />
      </Card>

      <Card title="Goals" subtitle={canAct ? "Open a goal to leave a comment on it." : undefined} padded={false}>
        <GoalList
          goals={data.goals}
          showAchievement={!pending}
          renderActions={goalEdit.active ? goalEdit.action : undefined}
          renderExtra={
            canAct
              ? (goal) =>
                  isReviewable(goal) && (
                    <FormField label="Comment on this goal" className="goal-comment-field">
                      <input
                        value={goalComments[goal.id] ?? goal.managerComments ?? ""}
                        onChange={(e) => setGoalComments((c) => ({ ...c, [goal.id]: e.target.value }))}
                        placeholder="Optional"
                      />
                    </FormField>
                  )
              : undefined
          }
        />
      </Card>

      {(changes.data || []).length > 0 && (
        <Card title="Goal change requests" subtitle="Changes the employee asked for on approved goals." padded={false}>
          <DataTable
            dense
            rows={changes.data}
            columns={[
              { key: "goal", label: "Goal", render: (r) => <span className="cell-stack"><strong>{r.goalTitle}</strong><small>{r.reason}</small></span> },
              { key: "proposed", label: "Proposed", render: (r) => `${r.proposed.item_title || ""} · ${r.proposed.weight ?? "—"}% · target ${r.proposed.target ?? "—"}` },
              { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} tone={r.status === "PENDING" ? "warning" : r.status === "APPROVED" ? "success" : "neutral"} /> },
              {
                key: "act", label: "", align: "right",
                render: (r) => r.status === "PENDING" && (
                  <span className="row-actions">
                    <Button size="sm" variant="ghost" onClick={() => setDeciding({ row: r, approve: false, reason: "" })}>Reject</Button>
                    {r.itemType === "GOAL"
                      ? <Button size="sm" onClick={() => setDeciding({ row: r, approve: true, reason: "" })}>Approve</Button>
                      : <small className="muted" title="KRA/KPI targets are set by HR">HR-owned {r.itemType}: only HR can change it</small>}
                  </span>
                ),
              },
            ]}
          />
        </Card>
      )}

      {canAct && (
        <Card title="Your decision">
          <FormField label="Overall comment" hint="Required when you request changes.">
            <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share what's good and what needs to change" />
          </FormField>
          <div className="decision-bar">
            <Button variant="secondary" icon={FiCornerUpLeft} onClick={() => setAction("changes")}>Request changes</Button>
            <Button variant="success" icon={FiCheck} disabled={data.totalWeightage !== 100 || data.weightIssues.length > 0} onClick={() => setAction("approve")}>Approve goals</Button>
          </div>
        </Card>
      )}

      <Card title="History">
        <ActivityTimeline items={data.history} />
      </Card>

      {goalEdit.element}

      <ConfirmDialog
        open={Boolean(deciding)}
        title={deciding?.approve ? "Approve this change?" : "Reject this change?"}
        message={deciding?.approve ? "The goal is updated. Because the sheet is already approved, the WHOLE goal sheet goes back to draft and the employee must resubmit it for your approval (progress history is kept)." : "The goal stays as approved."}
        confirmLabel={deciding?.approve ? "Approve change" : "Reject change"}
        tone={deciding?.approve ? "primary" : "danger"}
        loading={busy}
        onConfirm={decideChange}
        onClose={() => setDeciding(null)}
      >
        <FormField label="Note to the employee">
          <textarea rows={2} value={deciding?.reason || ""} onChange={(e) => setDeciding({ ...deciding, reason: e.target.value })} />
        </FormField>
      </ConfirmDialog>

      <ConfirmDialog
        open={Boolean(action)}
        title={action === "approve" ? "Approve these goals?" : "Send goals back for changes?"}
        message={
          action === "approve"
            ? `${data.employee.name} will be notified. Approved goals are locked; later changes need a change request that you approve.`
            : `${data.employee.name} will be able to edit and resubmit the goals.`
        }
        confirmLabel={action === "approve" ? "Approve goals" : "Request changes"}
        loading={busy}
        onConfirm={run}
        onClose={() => setAction(null)}
      />
    </div>
  );
}
